import type { StoreFormValues } from 'src/features/store/types/store.types';
import { instantToWallTime, wallTimeToInstant } from 'src/features/timezone';
import {
  DataDTOStore,
  DataDTOStoreCrud,
  DataEnumerationsDiscountCodeProvider,
  DataEnumerationsLookupMode,
  DataEnumerationsLoyaltyProgramProvider,
  DataEnumerationsPosSystem,
} from 'src/types/api/generated/api';

export const emptyStoreFormValues: StoreFormValues = {
  name: '',
  code: '',
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  phone: '',
  email: '',
  contactName: '',
  pin: '',
  orderIdleTimeoutInMinutes: '5',
  purchasedItemTimeoutPeriodInSeconds: '60',
  startListeningOnlyViaTrigger: 'false',
  notes: '',
  timezone: '',
  data1: '',
  loyaltyProgramMode: 'Disabled',
  loyaltyProgramPosSystem: '',
  loyaltyProgramProvider: '',
  loyaltyProgramMaximumAttempts: '',
  loyaltyProgramActiveFrom: '',
  loyaltyProgramActiveTo: '',
  loyaltyProgramButtonLabel: '',
  loyaltyProgramButtonColor: '',
  loyaltyProgramSuccessMessage: '',
  loyaltyProgramFailureMessage: '',
  loyaltyProgramHostname: '',
  loyaltyProgramUsername: '',
  loyaltyProgramPassword: '',
  clearLoyaltyProgramUsername: false,
  clearLoyaltyProgramPassword: false,
  discountCodeMode: 'Disabled',
  discountCodePosSystem: '',
  discountCodeProvider: '',
  discountCodeMaximumAttempts: '',
  discountCodeActiveFrom: '',
  discountCodeActiveTo: '',
  discountCodeButtonLabel: '',
  discountCodeButtonColor: '',
  discountCodeSuccessMessage: '',
  discountCodeFailureMessage: '',
};

export const fromStoreView = (s: DataDTOStore): StoreFormValues => {
  const storeZone = s.timezone;

  return {
    name: s.name,
    code: s.code ?? '',
    address1: s.address?.address1 ?? '',
    address2: s.address?.address2 ?? '',
    city: s.address?.city ?? '',
    state: s.address?.state ?? '',
    zip: s.address?.zip ?? '',
    phone: s.phone ?? '',
    email: s.email ?? '',
    contactName: s.contactName ?? '',
    pin: '',
    orderIdleTimeoutInMinutes: String(s.orderIdleTimeoutInMinutes ?? 5),
    purchasedItemTimeoutPeriodInSeconds: String(s.purchasedItemTimeoutPeriodInSeconds ?? 60),
    startListeningOnlyViaTrigger: String(s.startListeningOnlyViaTrigger ?? false),
    notes: s.notes ?? '',
    timezone: s.timezone,
    data1: s.data1 ?? '',
    loyaltyProgramMode: s.loyaltyProgramMode,
    loyaltyProgramPosSystem: s.loyaltyProgramPosSystem ?? '',
    loyaltyProgramProvider: s.loyaltyProgramProvider ?? '',
    loyaltyProgramMaximumAttempts:
      s.loyaltyProgramMaximumAttempts == null ? '' : String(s.loyaltyProgramMaximumAttempts),
    loyaltyProgramActiveFrom: s.loyaltyProgramActiveFrom
      ? instantToWallTime(s.loyaltyProgramActiveFrom, storeZone)
      : '',
    loyaltyProgramActiveTo: s.loyaltyProgramActiveTo
      ? instantToWallTime(s.loyaltyProgramActiveTo, storeZone)
      : '',
    loyaltyProgramButtonLabel: s.loyaltyProgramButtonLabel ?? '',
    loyaltyProgramButtonColor: s.loyaltyProgramButtonColor ?? '',
    loyaltyProgramSuccessMessage: s.loyaltyProgramSuccessMessage ?? '',
    loyaltyProgramFailureMessage: s.loyaltyProgramFailureMessage ?? '',
    loyaltyProgramHostname: s.loyaltyProgramHostname ?? '',
    loyaltyProgramUsername: '',
    loyaltyProgramPassword: '',
    clearLoyaltyProgramUsername: false,
    clearLoyaltyProgramPassword: false,
    discountCodeMode: s.discountCodeMode,
    discountCodePosSystem: s.discountCodePosSystem ?? '',
    discountCodeProvider: s.discountCodeProvider ?? '',
    discountCodeMaximumAttempts:
      s.discountCodeMaximumAttempts == null ? '' : String(s.discountCodeMaximumAttempts),
    discountCodeActiveFrom: s.discountCodeActiveFrom
      ? instantToWallTime(s.discountCodeActiveFrom, storeZone)
      : '',
    discountCodeActiveTo: s.discountCodeActiveTo
      ? instantToWallTime(s.discountCodeActiveTo, storeZone)
      : '',
    discountCodeButtonLabel: s.discountCodeButtonLabel ?? '',
    discountCodeButtonColor: s.discountCodeButtonColor ?? '',
    discountCodeSuccessMessage: s.discountCodeSuccessMessage ?? '',
    discountCodeFailureMessage: s.discountCodeFailureMessage ?? '',
  };
};

export const toStoreCrudPayload = (v: StoreFormValues, clientId: number): DataDTOStoreCrud => {
  const storeZone = v.timezone;

  return {
    name: v.name,
    code: v.code || null,
    contactName: v.contactName || null,
    phone: v.phone || null,
    email: v.email || null,
    notes: v.notes || null,
    address: {
      address1: v.address1 || null,
      address2: v.address2 || null,
      city: v.city || null,
      state: v.state || null,
      zip: v.zip || null,
      storeId: 0,
    },
    timezone: v.timezone,
    pin: v.pin || null,
    clientId,
    startListeningOnlyViaTrigger: v.startListeningOnlyViaTrigger === 'true',
    orderIdleTimeoutInMinutes:
      v.orderIdleTimeoutInMinutes === '' ? null : Number(v.orderIdleTimeoutInMinutes),
    purchasedItemTimeoutPeriodInSeconds:
      v.purchasedItemTimeoutPeriodInSeconds === ''
        ? null
        : Number(v.purchasedItemTimeoutPeriodInSeconds),
    data1: v.data1 || null,
    loyaltyProgramMode: v.loyaltyProgramMode as DataEnumerationsLookupMode,
    loyaltyProgramPosSystem:
      (v.loyaltyProgramPosSystem as DataEnumerationsPosSystem) || null,
    loyaltyProgramProvider:
      (v.loyaltyProgramProvider as DataEnumerationsLoyaltyProgramProvider) || null,
    loyaltyProgramMaximumAttempts:
      v.loyaltyProgramMaximumAttempts === ''
        ? null
        : Number(v.loyaltyProgramMaximumAttempts),
    loyaltyProgramActiveFrom: v.loyaltyProgramActiveFrom
      ? wallTimeToInstant(v.loyaltyProgramActiveFrom, storeZone)
      : null,
    loyaltyProgramActiveTo: v.loyaltyProgramActiveTo
      ? wallTimeToInstant(v.loyaltyProgramActiveTo, storeZone)
      : null,
    loyaltyProgramButtonLabel: v.loyaltyProgramButtonLabel || null,
    loyaltyProgramButtonColor: v.loyaltyProgramButtonColor || null,
    loyaltyProgramSuccessMessage: v.loyaltyProgramSuccessMessage || null,
    loyaltyProgramFailureMessage: v.loyaltyProgramFailureMessage || null,
    loyaltyProgramHostname: v.loyaltyProgramHostname || null,
    loyaltyProgramUsername: v.loyaltyProgramUsername || undefined,
    loyaltyProgramPassword: v.loyaltyProgramPassword || undefined,
    clearLoyaltyProgramUsername: v.clearLoyaltyProgramUsername,
    clearLoyaltyProgramPassword: v.clearLoyaltyProgramPassword,
    discountCodeMode: v.discountCodeMode as DataEnumerationsLookupMode,
    discountCodePosSystem: (v.discountCodePosSystem as DataEnumerationsPosSystem) || null,
    discountCodeProvider:
      (v.discountCodeProvider as DataEnumerationsDiscountCodeProvider) || null,
    discountCodeMaximumAttempts:
      v.discountCodeMaximumAttempts === '' ? null : Number(v.discountCodeMaximumAttempts),
    discountCodeActiveFrom: v.discountCodeActiveFrom
      ? wallTimeToInstant(v.discountCodeActiveFrom, storeZone)
      : null,
    discountCodeActiveTo: v.discountCodeActiveTo
      ? wallTimeToInstant(v.discountCodeActiveTo, storeZone)
      : null,
    discountCodeButtonLabel: v.discountCodeButtonLabel || null,
    discountCodeButtonColor: v.discountCodeButtonColor || null,
    discountCodeSuccessMessage: v.discountCodeSuccessMessage || null,
    discountCodeFailureMessage: v.discountCodeFailureMessage || null,
  };
};
